import { getElementComponent, getElementFromStateById, createElementFromCopy } from '../ContentElements';
import { getParentElement, transformElementValue } from '../ContentElements';
import hideLog from 'src/utils/log.js';

function TableBuildHead(tableId, data, parserFn) {
  const tableHead = parserFn(data, [], false).reduce(
    (acc, next) => {
      next.parentId = tableId;
      acc.tableHeadElements[next.id] = next;
      acc.tableHeadOrder.push(next.id);
      return acc;
    },
    { tableHeadElements: {}, tableHeadOrder: [] },
  );
  return tableHead;
}

// we may remove this watch function
// since the header column will always be present
function TableWatchShowLogic(getUpdateColumnFn) {
  return colIndex => newState => {
    //const updateFn = getUpdateColumnFn(colIndex);
    //updateFn('visible', newState.isComponentVisible);
  };
}

function TableGetChildren(tableHeadElements, elementActions) {
  const { getParserState } = elementActions();
  const { elements } = getParserState();

  let tableElements = [];
  let otherElements = [];

  elements.forEach(el => {
    if (tableHeadElements[el.logicId] && tableHeadElements[el.logicId].parentId === el.parentId) {
      tableElements.push(el);
    } else {
      otherElements.push(el);
    }
  });

  return { tableElements, otherElements };
}

function TableRebuildModel(newRows, otherElements, tableHeadElements, elementActions, tableActions, controlTypeName) {
  const { setParserState, getParserState, refreshShowLogic, callOnChange } = elementActions();

  // rebuild the table rows
  const { rowsElements, rowsValues } = TableBuildRows(newRows, tableHeadElements, elementActions, tableActions);

  // refresh the parser state to reflect the newly added row in the first row position
  setParserState(otherElements.concat(rowsElements));
  callOnChange();

  // update the parent component props
  const { elements, elementsIndex } = getParserState();
  const colName = Object.keys(tableHeadElements)[0];
  const colEl = tableHeadElements[colName];

  const parentNode = getParentElement(colEl, elements, elementsIndex);
  const parentUpdateFn = parentNode.updateComponent;

  const newColumns = TableBuildColumnOptions(
    tableHeadElements,
    elementActions,
    tableActions,
    parentNode.controlTypeName || controlTypeName,
  );

  // refresh the table to reflect the new rows
  parentUpdateFn({ columns: newColumns, rows: rowsValues });

  // refresh showLogic on newly rebuilt model
  refreshShowLogic();

  //refresh the table head to show hidden columns
  const updatedColumns = refreshColumnsOptions(getParserState().elements, newColumns, rowsValues);
  parentUpdateFn({ columns: updatedColumns, rows: rowsValues });
}

function refreshColumnsOptions(elements, newColumns, rowsValues) {
  let updatedColumns = [];

  // if there are no elements it's an empty table
  // in that case we return the default columns
  if (elements && elements.length === 0) {
    return newColumns;
  }

  // when there are no rows we need to show the default columns
  if (rowsValues && rowsValues.length === 0) {
    return newColumns;
  }
  // go through each element and search by `logicId` and determine if there is one visible element.
  // if there is a visible element then we are mark the table column `visible` to `true`

  newColumns.forEach(currentColumn => {
    let currentElements = elements.filter(elm => {
      return elm.logicId === currentColumn.id;
    });

    if (currentElements.length) {
      let visibleElements = currentElements.filter(obj => {
        return obj.props.isComponentVisible === true;
      });

      currentColumn.visible = visibleElements.length > 0;
      if (visibleElements && visibleElements.length > 0) {
        visibleElements.forEach(value => {
          if (value.props.controlTypeName === 'Caption') {
            currentColumn.visible = false;
          }
        });
      }
      updatedColumns.push(currentColumn);
    }
  });

  return updatedColumns;
}

function TableAddRow(
  defaultValues,
  tableHeadElements,
  tableHeadOrder,
  elementActions,
  tableActions,
  emptyColume = false,
  isHTComboAndFromDelete = false,
  controlTypeName,
) {
  let { tableElements, otherElements } = TableGetChildren(tableHeadElements, elementActions);
  const { getParserState, setFormValue, callOnChange, setFormValues } = elementActions();
  if (emptyColume) {
    tableElements = [];
  }
  const totalRows = tableElements.length / tableHeadOrder.length;
  let counter = 0;
  const { setRowId } = tableActions;
  const origRowIndex = setRowId();
  //const origRowIndex = !isHTComboAndFromDelete ? setRowId() : 0;

  let newRow = [];

  /* initialize formValues with new row and default values */
  const { elements, elementsIndex } = getParserState();

  tableHeadOrder.forEach((col, colIndex) => {
    const defaultValue = defaultValues && defaultValues[colIndex] ? defaultValues[colIndex] : '';

    newRow.push({
      elementName: col,
      value: (defaultValues && defaultValues[colIndex]) || '',
      origRowIndex,
      docTypeCode: null,
    });

    const { logicId, parentId } = tableHeadElements[col];

    let formValue = transformElementValue({ ...tableHeadElements[col], origRowIndex }, defaultValue, { elements, elementsIndex }, false);
    formValue.isNewRow = true;

    //if(!isHTComboAndFromDelete)
    setFormValue(logicId, formValue, parentId, origRowIndex, colIndex);
    callOnChange();
  });

  const newRows = Array.from({ length: totalRows }).reduce(
    (acc, next, index) => {
      const colData = tableHeadOrder.map((col, colIndex) => ({
        ...(tableElements[counter].answerPageId && {
          answerPageId: tableElements[counter].answerPageId,
        }),
        elementName: tableElements[counter].logicId,
        origRowIndex: tableElements[counter].origRowIndex,
        value: tableElements[counter++].props.value,
        docTypeCode: null,
      }));

      acc[index + 1] = colData;
      return acc;
    },
    { 0: newRow },
  );
  TableRebuildModel(newRows, otherElements, tableHeadElements, elementActions, tableActions, controlTypeName);
  //if(!isHTComboAndFromDelete)
  setFormValues();
  callOnChange();
}

function TableDeleteRows(rows, tableHeadElements, tableHeadOrder, elementActions, tableActions, tableType) {
  const { tableElements, otherElements } = TableGetChildren(tableHeadElements, elementActions);

  const rowsToRemove = rows.map(row => row.origRowIndex);
  const totalRows = tableElements.length / tableHeadOrder.length;

  let elementCounter = 0;
  let rowCounter = 0;

  let newRows = Array.from({ length: totalRows }).reduce((acc, next, index) => {
    const docTypeCode = tableElements[elementCounter].docTypeCode;
    const colData = tableHeadOrder.map((col, colIndex) => ({
      ...(tableElements[elementCounter].answerPageId && {
        answerPageId: tableElements[elementCounter].answerPageId,
      }),
      elementName: tableElements[elementCounter].logicId,
      origRowIndex: tableElements[elementCounter].origRowIndex,
      value: tableElements[elementCounter++].props.value,
      docTypeCode,
    }));

    if (rowsToRemove.includes(index)) {
      const { getParserState, setFormValue } = elementActions();
      const { elements, elementsIndex } = getParserState();

      const elStartPosition = elementCounter - tableHeadOrder.length;

      // update the isDeleted flag in formValues
      tableHeadOrder.forEach((col, colIndex) => {
        const targetEl = tableElements[elStartPosition + colIndex];
        const formValue = transformElementValue(targetEl, targetEl.defaultValue, { elements, elementsIndex }, true);

        setFormValue(
          targetEl.logicId,
          {
            ...formValue,
            value: '',
            AnswerPageID: targetEl.answerPageId,
            SourceTableId: targetEl.sourceTableId,
            isDeleted: true,
          },
          targetEl.parentId,
          targetEl.origRowIndex,
          colIndex,
        );
      });

      return acc;
    }

    acc[rowCounter++] = colData;
    return acc;
  }, {});

  TableRebuildModel(newRows, otherElements, tableHeadElements, elementActions, tableActions, tableType);

  // call onChange so outside gets the latest isFormComplete
  const { callOnChange } = elementActions();
  callOnChange();
  if (Object.keys(newRows).length === 0) {
    let isHTComboAndFromDelete = tableType === 'HTCombo';
    //create empty row if all rows are deleted.
    TableAddRow(undefined, tableHeadElements, tableHeadOrder, elementActions, tableActions, true, isHTComboAndFromDelete, tableType);
  }
}

function TableBuildColumnOptions(tableHeadElements, elementActions, tableActions, controlTypeName) {
  let tableHeadColumnOptions = [];
  Object.entries(tableHeadElements).forEach(([_, col], index) => {
    tableHeadColumnOptions.push({
      id: col.id,
      displayText: col.label,
      sortable: controlTypeName !== 'HTCombo' && controlTypeName !== 'Horizontal' && controlTypeName !== 'Entity',
      visible: col.props.isComponentVisible,
      reviewerOnly: col.props.hasOwnProperty('reviewerOnly') ? col.props.reviewerOnly : false,
      displayUI: controlTypeName === 'HTCombo' ? col.displayUI : true, // Only we need this prop for Htcombo
      renderComponent: (value, meta, updateModelFn) => {
        return getElementFromStateById(col.id, meta.origRowIndex, elementActions);
      },

      helpText: col.props.helpText && controlTypeName !== 'HTCombo' ? col.props.helpText : '',
    });
  });
  return tableHeadColumnOptions;
}

function TableBuildForm(tableHeadElements, elementActions, rowIndex = '', defaultValues = {}, onDrawerClick, readOnly) {
  const { getParserState, setParserState, refreshShowLogic } = elementActions();

  let popupElements = [];
  let popupChildren = [];

  Object.entries(tableHeadElements).forEach(([key, element], index) => {
    const value = defaultValues[key] || element.props.value;
    const newElement = createElementFromCopy(
      element,
      undefined,
      {
        ...elementActions(),
        ...((element.controlTypeName === 'Date' || element.controlTypeName === 'DropDown' || element.controlTypeName === 'CountryList') &&
          onDrawerClick && {
            onChangeFn: () => {
              onDrawerClick(); // For Date control we are explicitly calling this function
            },
          }),
      },
      rowIndex,
      undefined,
      { rowIndex },
      { value, readOnly: element.props.readOnly },
    );
    popupElements.push(newElement);
    popupChildren.push(getElementComponent(newElement, index, false));
  });

  setParserState(getParserState().elements.concat(popupElements));
  refreshShowLogic();

  const { elements, elementsIndex } = getParserState();
  let refreshedElements = [];
  let refreshedChildren = [];

  popupElements.forEach((el, index) => {
    const newEl = elements[elementsIndex[el.id]];
    refreshedElements.push(newEl);
    refreshedChildren.push(getElementComponent(el, index, false, undefined, { ...newEl.props }));
  });

  return { elements: refreshedElements, children: refreshedChildren };
}

function TableBuildRow(data, origRowIndex, rowIndex, tableHeadElements, elementActions, tableActions) {
  let rowElements = [];
  let rowValues = [];

  const { getTableProps, getUpdateModelFn, onUpdateState, getRefreshTableFn } = tableActions;
  const { controlTypeName, hasReadOnly: isTableReadOnly } = getTableProps();
  const isHTComboTable = controlTypeName === 'HTCombo';

  // If the table row added is from table type 'HTCombo' then all the fields should be `Readonly` components
  // For empty row we only need redio buttons to be readonly and other componunts as it is
  data.forEach((col, colIndex) => {
    try {
      const docTypeCode = col.docTypeCode;
      const tableHeadElementName = tableHeadElements[col.elementName];
      const tableHeadElementControlTypeName = tableHeadElementName.controlTypeName;
      let columnEl;
      try {
        columnEl = createElementFromCopy(
          {
            ...tableHeadElements[col.elementName],
            ...(isHTComboTable && {
              controlTypeName: tableHeadElementControlTypeName !== 'Radio' ? tableHeadElementControlTypeName : 'ReadOnly',
            }),
            ...(isHTComboTable &&
              tableHeadElementControlTypeName !== 'Radio' && {
                controlTypeName: tableHeadElementControlTypeName,
              }),
          },
          col.value,
          {
            ...elementActions(),
            onChangeFn: () => {
              getUpdateModelFn(rowIndex, colIndex);
              getRefreshTableFn();
            },
            onUpdateState: onUpdateState ? onUpdateState(colIndex) : () => {},
          },
          rowIndex,
          undefined,
          {
            rowIndex,
            colIndex,
            origRowIndex: parseInt(origRowIndex, 10),
            ...(isHTComboTable && {
              origControlTypeName: tableHeadElements[col.elementName].controlTypeName,
            }),
            ...(col.answerPageId && { answerPageId: col.answerPageId }),
            ...(col.sourceTableId && { sourceTableId: col.sourceTableId }),
            docTypeCode,
          },
          {
            isHiddenMount: true,
            ...(isHTComboTable && {
              label:
                tableHeadElements[col.elementName].controlTypeName !== 'Radio' || col.value === ''
                  ? tableHeadElements[col.elementName].label
                  : undefined,
              helpText: undefined,
            }),
            readOnly: isHTComboTable || isTableReadOnly || tableHeadElements[col.elementName].props.readOnly,
            helpText: '',
            description: null,
            parentTableType: controlTypeName,
            docTypeCode,
            superScript: col.superScript1,
          },
        );
      } catch (errorElemnt) {
        if (!hideLog) {
          console.group('Error on CMS content in element');
          console.log(errorElemnt);
          console.log(tableHeadElementName);
          console.log(tableHeadElementControlTypeName);
          console.groupEnd('');
        }
      }

      rowElements.push({ ...columnEl, docTypeCode });
      rowValues.push(col.value);
    } catch (error) {
      if (!hideLog) {
        console.group('Error on CMS content');
        console.log(error);
        console.groupEnd('');
      }
    }
  });

  return { rowElements, rowValues };
}

function TableBuildRows(data, tableHeadElements, elementActions, tableActions) {
  if (!hideLog) {
    console.group('Table build rows');
    console.table(data, tableHeadElements, elementActions, tableActions);
    console.groupEnd();
  }
  let rowsElements = [];
  let rowsValues = [];

  Object.entries(data).forEach(([key, elements], rowIndex) => {
    // derive our origRowIndex from either the key or if it's provided later when defined
    let curEl = elements[0];

    const origRowIndex = curEl && curEl.origRowIndex !== undefined && curEl.origRowIndex !== null ? curEl.origRowIndex : key;
    const currentRow = TableBuildRow(elements, origRowIndex, rowIndex, tableHeadElements, elementActions, tableActions);

    rowsElements.push(...currentRow.rowElements);
    rowsValues.push(currentRow.rowValues);
  });

  return { rowsElements, rowsValues };
}

function TableDeleteRowInParser(elementsToRemove, elementActions) {
  const { getParserState, setParserState } = elementActions();
  const { elements: currentElements } = getParserState();

  const excludedElements = elementsToRemove.map(el => el.id);
  const cleanElements = currentElements.filter(el => !excludedElements.includes(el.id));
  setParserState(cleanElements);
}

function TableRefreshRow(elementsToRefresh, elementActions) {
  const { getParserState } = elementActions();
  const { elements, elementsIndex } = getParserState();

  const idsToRefresh = elementsToRefresh.map(el => el.id);
  idsToRefresh.forEach(id => {
    const position = elementsIndex[id];
    elements[position].updateComponent({ ...elements[position].props });
  });
}

function TableUpdateRowInParser(
  updatedElements,
  rowIndex,
  elementActions,
  tableHeadElements,
  tableHeadOrder,
  tableActions,
  isEmptyRow,
  controlTypeName = '',
) {
  const { getParserState, refreshShowLogic, setFormValues, callOnChange } = elementActions();
  const { elements: currentElements, logicIndex } = getParserState();
  const { setRowId } = tableActions;
  const { tableElements, otherElements } = TableGetChildren(tableHeadElements, elementActions);

  Object.entries(updatedElements).forEach(([key, val], index) => {
    const positions = logicIndex[key];

    positions.forEach(position => {
      let currentEl = currentElements[position];
      if (currentEl.rowIndex === rowIndex && val !== currentEl.props.value) {
        currentEl.props.value = val;
        currentEl.origRowIndex = isEmptyRow ? setRowId() : currentEl.origRowIndex;
        currentEl.updateComponent({ ...currentEl.props });
      }
    });
  });

  const totalRows = tableElements.length / tableHeadOrder.length;
  let counter = 0;
  const newRows = [];

  for (let index = 0; index < totalRows; index++) {
    let colData = [];
    for (let colIndex = 0; colIndex < tableHeadOrder.length; colIndex++) {
      const docTypeCode = tableElements[counter].docTypeCode;
      colData.push({
        ...(tableElements[counter].answerPageId && {
          answerPageId: tableElements[counter].answerPageId,
        }),
        elementName: tableElements[counter].logicId,
        origRowIndex: tableElements[counter].origRowIndex,
        value: tableElements[counter++].props.value,
        docTypeCode,
      });
    }
    newRows.push(colData);
  }

  TableRebuildModel(newRows, otherElements, tableHeadElements, elementActions, tableActions, controlTypeName);

  refreshShowLogic();
  setFormValues();
  callOnChange();
}

function getEmptyRow(tableHdr, tableType) {
  const emptyRowArray = tableHdr.map(header => {
    const emptyColume = {
      answerPageId: 0,
      sourceTableId: 0,
      elementName: '',
      value: '',
      isRowLocked: false,
      docTypeCode: null,
    };
    emptyColume.elementName = header.elementName ? `${header.elementName}` : header;
    return emptyColume;
  });

  return { 1: emptyRowArray };
}

function TableBuildEmptyRows(data, tableHeadElements, elementActions, tableActions, rowId) {
  let rowsElements = [];
  let rowsValues = [];

  Object.entries(data).forEach(([key, elements], rowIndex) => {
    // derive our origRowIndex from either the key or if it's provided later when defined
    let curEl = elements[0];

    const origRowIndex = curEl && curEl.origRowIndex ? curEl.origRowIndex : rowId;
    const currentRow = TableBuildRow(elements, origRowIndex, rowIndex, tableHeadElements, elementActions, tableActions);

    rowsElements.push(...currentRow.rowElements);
    rowsValues.push(currentRow.rowValues);
  });

  return { rowsElements, rowsValues };
}

export {
  TableBuildHead,
  TableAddRow,
  TableGetChildren,
  TableDeleteRows,
  TableWatchShowLogic,
  TableBuildForm,
  TableRefreshRow,
  TableBuildColumnOptions,
  TableDeleteRowInParser,
  TableBuildRows,
  TableUpdateRowInParser,
  refreshColumnsOptions,
  getEmptyRow,
  TableBuildEmptyRows,
};



// WEBPACK FOOTER //
// ./src/ui/library/ContentParser/elements/TableFactoryHelpers.js